import { mergeClassNames } from "../../utils/classes";
import { getAlignSelfClassName, getIconSize, getMarginClassName } from "../Button/button.helper";
import Icon from "../Icon/Icon";
import { getSizeClassName } from "./iconButton.helper";

import type { FontelloIcons } from "../../assets/fontello";
import type { ButtonLinkComponentType } from "../Button/Button";
import type {
	CommonButtonProps,
	ExternalLinkButtonProps,
	LinkButtonProps,
	NormalButtonProps,
} from "../Button/button.types";

// prettier-ignore
import * as styles from "./iconButton.module.scss";
// prettier-ignore
import * as baseStyles from "../Button/button.base.module.scss";

import type { MouseEvent, Ref } from "react";

interface IconButtonProps extends CommonButtonProps {
	icon: FontelloIcons;
}

type Props = IconButtonProps & (NormalButtonProps | LinkButtonProps | ExternalLinkButtonProps);

let LinkComponent: ButtonLinkComponentType | null = null;

export const initializeIconButtonLinkComponent = (linkComponentImplementation: ButtonLinkComponentType) => {
	LinkComponent = linkComponentImplementation;
};

export const IconButton = ({
	ref,
	size = "normal",
	icon,
	disabled,
	testId,
	id,
	title,
	autoFocus,
	align,
	margin,
	className,
	...props
}: Props & { ref?: Ref<HTMLButtonElement | HTMLAnchorElement> }) => {
	const classNames = mergeClassNames(
		className,
		baseStyles.button,
		baseStyles.secondary,
		styles.iconButton,
		getSizeClassName(size),
		disabled && baseStyles.disabled,
		disabled && baseStyles.disabledSecondary,
		align && getAlignSelfClassName(align),
		margin && getMarginClassName(margin),
	);

	const commonProps = {
		id,
		title,
		className: classNames,
		"data-testid": testId,
	};

	const iconNode = <Icon name={icon} size={getIconSize(size)} className={baseStyles.icon} />;

	if (props.type === "link" && !props.external) {
		if (!LinkComponent) {
			throw new Error(
				"LinkComponent is not initialized. Please call initializeIconButtonLinkComponent in your application to set it up.",
			);
		}

		return (
			<LinkComponent
				{...commonProps}
				onClick={(e: MouseEvent) => (disabled ? e.preventDefault() : props.onNavigation?.())}
				to={props.route}
				ref={ref as Ref<HTMLAnchorElement>}
			>
				{iconNode}
			</LinkComponent>
		);
	}

	if (props.type === "link" && props.external) {
		return (
			<a
				{...commonProps}
				onClick={e => (disabled ? e.preventDefault() : props.onNavigation?.())}
				href={props.route}
				download={props.download}
				target={props.target}
				ref={ref as Ref<HTMLAnchorElement>}
			>
				{iconNode}
			</a>
		);
	}

	return (
		<button
			{...commonProps}
			onClick={disabled ? undefined : props.onClick}
			disabled={disabled}
			autoFocus={autoFocus}
			ref={ref as Ref<HTMLButtonElement>}
			type="button"
		>
			{iconNode}
		</button>
	);
};
